import React from 'react';
import { Card, CardContent } from '../components/shadcn/card';
import { Separator } from '../components/shadcn/separator';
import LegalPageLayout from '../components/LegalPageLayout';

const Privacy: React.FC = () => {
  return (
    <LegalPageLayout
      badge="Legal"
      title="Privacy Policy"
      subtitle="How we collect, use, and protect your personal data"
      effectiveDate="1 January 2026"
      lastUpdated="March 2026"
      currentPage="privacy"
    >
      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">1. Introduction</h2>
        <p>
          At Nucleus Systems, we believe that privacy is a fundamental right, not an afterthought. This
          Privacy Policy ("Policy") applies to personal data collected by Nucleus Systems and our
          affiliates ("Paxley", "we", "us", or "our") when you interact with our websites ("Sites"), use
          our product Paxley ("Services"), or engage with us through channels such as customer support,
          events, marketing, or product research.
        </p>
        <p className="mt-4">
          Paxley is the product name of the security product built by Nucleus Systems. Throughout this
          document, "Paxley" and "Nucleus Systems" are used interchangeably. This Policy applies only to
          personal data for which Nucleus Systems is the data controller.
        </p>
        <p className="mt-4">
          We regard the EU General Data Protection Regulation (GDPR) as the gold standard for data
          protection globally. This Policy also reflects South Africa's Protection of Personal
          Information Act (POPIA) of 2013, the California Consumer Privacy Act (CCPA) of 2018, and the
          UK Data Protection Act 2018. Where POPIA introduces requirements that differ from GDPR, those
          differences are noted in the relevant sections below.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">2. Data Controller &amp; Information Officer</h2>
        <p>
          Nucleus Systems is the data controller responsible for your personal data under GDPR. Under
          POPIA, Nucleus Systems designates an Information Officer accountable for ensuring compliance
          with POPIA obligations.
        </p>
        <Card className="border-border/30 bg-surface2 mt-4">
          <CardContent className="p-6 space-y-2 text-sm">
            <p><strong className="text-text">Contact:</strong> Nucleus Systems (Pty) Ltd</p>
            <p>43 Silver Creek Eco Park Estates</p>
            <p><strong className="text-text">Tel:</strong> 083 680 3427</p>
            <p>
              <strong className="text-text">Email:</strong>{' '}
              <a href="mailto:godfrey.kutumela@nucleus-systems.com" className="text-brand hover:underline">
                godfrey.kutumela@nucleus-systems.com
              </a>
            </p>
            <p>
              <strong className="text-text">Web:</strong>{' '}
              <a href="https://www.nucleus-systems.com" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">
                www.nucleus-systems.com
              </a>
            </p>
            <p><strong className="text-text">Information Officer:</strong> Godfrey Kutumela</p>
          </CardContent>
        </Card>
        <Card className="border-border/40 bg-surface2 mt-4">
          <CardContent className="p-4">
            <p className="text-text2 text-sm">
              <strong>Note:</strong> South African users may direct POPIA-related queries or complaints
              to our Information Officer at the contact details above.
            </p>
          </CardContent>
        </Card>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">3. Information We Collect</h2>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">3.1 Personal Identification Information</h3>
        <p>When you register or interact with our Service via GitHub OAuth, we may collect:</p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li>GitHub display name, username, email address, and avatar URL.</li>
          <li>No passwords are stored; authentication is handled entirely through GitHub OAuth.</li>
          <li>Session tokens are cryptographically hashed.</li>
        </ul>
        <p className="mt-4">
          When you interact with our Site, we may collect your first name, email address, and your
          support message.
        </p>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">3.2 Payment Information</h3>
        <p>When you subscribe to a paid plan, we collect:</p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li>Billing email address per organisation.</li>
          <li>Subscription status and invoice history.</li>
          <li>Payment card details are handled entirely by <strong className="text-text">PAYSTACK</strong> (PCI-DSS compliant). Paxley never sees or stores card numbers.</li>
          <li>We do not store billing names or billing addresses.</li>
        </ul>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">3.3 GitHub Repository Data</h3>
        <p>
          Our Service offers code scanning features. With your explicit authorisation, we request
          temporary read-only access to your selected GitHub repositories. We process repository names,
          metadata, and grant temporary read access to source code during scans. Code is cloned,
          scanned, and then immediately deleted — source code is never stored persistently. Only scan
          findings metadata is retained.
        </p>
        <p className="mt-4">
          You may revoke GitHub access at any time through GitHub account settings (Settings &gt;
          Applications &gt; Authorised OAuth Apps) or from within the Paxley platform.
        </p>

        <Card className="border-emerald-500/30 bg-emerald-500/10 mt-4">
          <CardContent className="p-4">
            <p className="text-emerald-700 dark:text-emerald-300 font-medium">
              We do NOT store your source code. Code is temporarily cloned for analysis and immediately
              deleted after the scan completes.
            </p>
          </CardContent>
        </Card>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">3.4 Usage and Analytics Data</h3>
        <p>
          We do not collect IP addresses, browser information, pages visited, referring URLs, or
          clickstream data. We do not use third-party analytics tools (e.g., Google Analytics, Mixpanel)
          when you use the Service. For cookie-related analytics details, see our{' '}
          <a href="/cookies" className="text-brand hover:underline">Cookie Policy</a>.
        </p>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">3.5 Audit Logging</h3>
        <p>
          We log user actions (e.g., triggering scans, suppressing findings) with timestamps for
          security and compliance purposes. These logs do not contain IP addresses or device
          information.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">4. How We Use Your Information</h2>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">4.1 To Provide and Operate the Service</h3>
        <ul className="list-disc list-inside space-y-2">
          <li>Create and manage your account.</li>
          <li>Process payments and manage subscriptions.</li>
          <li>Perform code scanning and deliver scan results.</li>
          <li>Respond to your support requests and inquiries.</li>
        </ul>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">4.2 To Improve the Service</h3>
        <ul className="list-disc list-inside space-y-2">
          <li>Analyse usage patterns to improve functionality and user experience.</li>
          <li>Conduct internal research and product development.</li>
          <li>Monitor and resolve technical issues.</li>
        </ul>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">4.3 To Communicate With You</h3>
        <ul className="list-disc list-inside space-y-2">
          <li>Send product updates, security alerts, and administrative messages.</li>
          <li>Send marketing communications where you have given consent (you may opt out at any time).</li>
        </ul>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">4.4 Legal Compliance and Safety</h3>
        <ul className="list-disc list-inside space-y-2">
          <li>Comply with applicable laws and regulations.</li>
          <li>Enforce our Terms of Service.</li>
          <li>Detect, prevent, and address fraud or security incidents.</li>
        </ul>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">5. Legal Basis for Processing</h2>
        <p>
          For users in the European Economic Area (EEA) and the United Kingdom, we process personal data
          under the following GDPR legal bases:
        </p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li><strong className="text-text">Contract Performance:</strong> Processing necessary to fulfil our contract with you (e.g., providing the Service, processing payments).</li>
          <li><strong className="text-text">Legitimate Interests:</strong> Processing necessary for our legitimate business interests such as improving the Service and preventing fraud, where these interests are not overridden by your rights.</li>
          <li><strong className="text-text">Consent:</strong> Where you have given explicit consent (e.g., marketing communications, GitHub repository access). You may withdraw consent at any time.</li>
          <li><strong className="text-text">Legal Obligation:</strong> Processing necessary to comply with a legal obligation.</li>
        </ul>
        <Card className="border-border/40 bg-surface2 mt-4">
          <CardContent className="p-4">
            <p className="text-text2 text-sm">
              <strong>Note:</strong> For South African users, processing of personal information is
              governed by POPIA. The lawful grounds under POPIA largely mirror those above. Where these
              grounds differ in their application, Nucleus Systems will apply the standard that is most
              protective of your rights.
            </p>
          </CardContent>
        </Card>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">6. Sharing of Your Information</h2>
        <p>We do not sell your personal data. We may share information in the following circumstances:</p>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">6.1 Service Providers</h3>
        <p>
          We engage trusted third-party companies to perform functions on our behalf, such as payment
          processing, cloud hosting, and email delivery. These providers are contractually bound to
          process data only as instructed and in compliance with applicable law.
        </p>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">6.2 GitHub Integration</h3>
        <p>
          Repository data accessed via GitHub OAuth is processed solely by Nucleus Systems and is not
          shared with any third party except as required to deliver the scanning feature (e.g., our
          cloud infrastructure provider).
        </p>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">6.3 Legal Requirements</h3>
        <p>
          We may disclose your information if required by law, court order, or governmental authority,
          or when we believe disclosure is necessary to protect our rights or the safety of others.
        </p>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">6.4 Business Transfers</h3>
        <p>
          In the event of a merger, acquisition, or sale of substantially all of our assets, your
          personal data may be transferred. We will notify you before your data becomes subject to a
          different privacy policy.
        </p>

        <Card className="border-emerald-500/30 bg-emerald-500/10 mt-4">
          <CardContent className="p-4">
            <p className="text-emerald-700 dark:text-emerald-300 font-medium">
              We do NOT sell your personal information to third parties.
            </p>
          </CardContent>
        </Card>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">7. International Data Transfers</h2>
        <p>
          Nucleus Systems may transfer your personal data to countries outside the EEA. Where we do so,
          we ensure appropriate safeguards are in place, including Standard Contractual Clauses (SCCs)
          approved by the European Commission and adequacy decisions.
        </p>
        <Card className="border-border/40 bg-surface2 mt-4">
          <CardContent className="p-4">
            <p className="text-text2 text-sm">
              <strong>Note:</strong> Under POPIA, Nucleus Systems may only transfer personal information
              about South African data subjects to a foreign country if that country has adequate data
              protection laws in place, or if the recipient is bound by enforceable data protection
              obligations equivalent to those under POPIA.
            </p>
          </CardContent>
        </Card>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">8. Cookies and Tracking Technologies</h2>
        <p>
          We use a limited set of cookies strictly necessary for the Service to function, such as
          session management and authentication. We do not use marketing cookies, nor do we deploy
          third-party tracking technologies within the Service itself.
        </p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li><strong className="text-text">Strictly Necessary Cookies:</strong> Essential for the Service to function (e.g., session management, authentication).</li>
          <li><strong className="text-text">Preference Cookies:</strong> Remember your settings and preferences.</li>
        </ul>
        <p className="mt-4">
          For full details on cookies, your choices, and browser-based opt-out signals, please refer to
          our <a href="/cookies" className="text-brand hover:underline">Cookie Policy</a>.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">9. Data Retention</h2>
        <p>
          Nucleus Systems holds personal data only for as long as it serves the purpose for which it was
          originally collected — whether delivering our platform, maintaining your account, or meeting
          legal and business requirements. When data is no longer needed, we securely delete,
          anonymise, or dispose of it in line with our internal policies.
        </p>
        <p className="mt-4">
          The only data Nucleus Systems stores about your codebase is the results of your security
          scans — we never retain a copy of your source code. Clients have full control over their scan
          results and can permanently delete this data from within the platform at any time.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">10. Your Privacy Rights</h2>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">10.1 Rights Under GDPR (EEA &amp; UK)</h3>
        <ul className="list-disc list-inside space-y-2">
          <li><strong className="text-text">Right of Access:</strong> Request a copy of the personal data we hold about you.</li>
          <li><strong className="text-text">Right to Rectification:</strong> Request correction of inaccurate or incomplete data.</li>
          <li><strong className="text-text">Right to Erasure ("Right to be Forgotten"):</strong> Request deletion of your personal data, subject to certain exceptions.</li>
          <li><strong className="text-text">Right to Restriction:</strong> Request that we limit the processing of your data in certain circumstances.</li>
          <li><strong className="text-text">Right to Data Portability:</strong> Receive your data in a structured, machine-readable format.</li>
          <li><strong className="text-text">Right to Object:</strong> Object to processing based on legitimate interests or for direct marketing.</li>
          <li><strong className="text-text">Right to Withdraw Consent:</strong> Where processing is based on consent, withdraw it at any time.</li>
          <li><strong className="text-text">Right to Lodge a Complaint:</strong> File a complaint with your national Data Protection Authority.</li>
        </ul>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">10.2 Rights Under POPIA (South Africa)</h3>
        <ul className="list-disc list-inside space-y-2">
          <li><strong className="text-text">Right of Access:</strong> Request confirmation and a description of the personal information we hold about you.</li>
          <li><strong className="text-text">Right to Correction or Deletion:</strong> Request that inaccurate, irrelevant, or outdated information be corrected or deleted.</li>
          <li><strong className="text-text">Right to Object:</strong> Object to the processing of your personal information on reasonable grounds.</li>
          <li><strong className="text-text">Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is consent-based.</li>
          <li><strong className="text-text">Right to Complain:</strong> Lodge a complaint with the Information Regulator of South Africa.</li>
        </ul>
        <Card className="border-border/40 bg-surface2 mt-4">
          <CardContent className="p-4">
            <p className="text-text2 text-sm">
              <strong>Note:</strong> Unlike GDPR, POPIA does not provide a formal right to data
              portability.
            </p>
          </CardContent>
        </Card>
        <p className="mt-4">
          To exercise any of these rights, contact us at{' '}
          <a href="mailto:godfrey.kutumela@nucleus-systems.com" className="text-brand hover:underline">
            godfrey.kutumela@nucleus-systems.com
          </a>
          . We will respond within 30 days. We may need to verify your identity before processing your
          request.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">11. Security of Your Information</h2>
        <p>
          We implement industry-standard technical and organisational security measures to protect your
          personal data, including:
        </p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li>Encryption of data in transit (TLS/HTTPS) and at rest (AES-256).</li>
          <li>Access controls and multi-factor authentication for internal systems.</li>
          <li>Regular security assessments and penetration testing.</li>
          <li>Employee training on data protection and security best practices.</li>
        </ul>
        <p className="mt-4">
          No method of transmission over the Internet or electronic storage is 100% secure. While we
          strive to use commercially acceptable means to protect your data, we cannot guarantee
          absolute security.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">12. Children's Privacy</h2>
        <p>
          The Service is not directed to minors. Under GDPR, we do not knowingly collect personal data
          from children under the age of 16 without verifiable parental consent. Under POPIA, Nucleus
          Systems does not process personal information of children under the age of 18 without the
          consent of a competent person (i.e., a parent or guardian).
        </p>
        <p className="mt-4">
          If you believe we have inadvertently collected personal data from a minor, please contact us
          immediately at{' '}
          <a href="mailto:godfrey.kutumela@nucleus-systems.com" className="text-brand hover:underline">
            godfrey.kutumela@nucleus-systems.com
          </a>{' '}
          and we will take prompt steps to delete it.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">13. Third-Party Links and Services</h2>
        <p>
          The Service may contain links to third-party websites, integrations, or services (including
          GitHub). This Privacy Policy does not apply to those third parties. We encourage you to review
          the privacy policies of any third-party services you use in connection with Nucleus Systems.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">14. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices,
          technology, or legal requirements. We will notify you of material changes by posting the
          updated Policy on this page with a revised Effective Date and sending an email notification
          to registered users.
        </p>
        <p className="mt-4">
          Your continued use of the Service after the effective date of the revised Policy constitutes
          your acceptance of the changes.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">15. Contact Us</h2>
        <p>For questions, concerns, or requests regarding this Privacy Policy or our data practices:</p>
        <Card className="border-border/30 bg-surface2 mt-4">
          <CardContent className="p-6 space-y-2 text-sm">
            <p><strong className="text-text">Contact:</strong> Nucleus Systems (Pty) Ltd</p>
            <p>43 Silver Creek Eco Park Estates</p>
            <p><strong className="text-text">Tel:</strong> 083 680 3427</p>
            <p>
              <strong className="text-text">Email:</strong>{' '}
              <a href="mailto:godfrey.kutumela@nucleus-systems.com" className="text-brand hover:underline">
                godfrey.kutumela@nucleus-systems.com
              </a>
            </p>
            <p><strong className="text-text">Web Developer:</strong> Godfrey Kutumela</p>
            <p className="pt-2 text-muted">
              For EEA residents: lodge a complaint with your national Data Protection Authority.
            </p>
          </CardContent>
        </Card>
        <p className="mt-4">
          For South African residents, complaints may be lodged with the Information Regulator of South
          Africa:
        </p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li>
            <strong className="text-text">Website:</strong>{' '}
            <a href="https://www.inforegulator.org.za" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">
              www.inforegulator.org.za
            </a>
          </li>
          <li>
            <strong className="text-text">Email:</strong>{' '}
            <a href="mailto:inforeg@justice.gov.za" className="text-brand hover:underline">
              inforeg@justice.gov.za
            </a>
          </li>
        </ul>
        <p className="mt-6 text-sm text-muted">© 2026 Nucleus Systems (Pty) Ltd. All rights reserved.</p>
      </section>
    </LegalPageLayout>
  );
};

export default Privacy;
